class CountdownTimer extends HTMLElement {
  constructor() {
    super();
    this.timeLeft = {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0
    };
    this.timer = null;
    this.elements = {};
    this.isExpired = false;

    // Expiry behavior settings
    this.hideWhenExpired = false;

    // Default translations
    this.translations = {
      days: {
        singular: 'TAG',
        plural: 'TAGE'
      },
      hours: {
        singular: 'STD',
        plural: 'STD'
      },
      minutes: {
        singular: 'MIN',
        plural: 'MIN'
      },
      seconds: {
        singular: 'SEK',
        plural: 'SEK'
      }
    };
  }

  static get observedAttributes() {
    return ['target-date', 'translations', 'hide-when-expired'];
  }

  connectedCallback() {
    this.hideWhenExpired = this.getAttribute('hide-when-expired') === 'true';
    this.loadTranslations();
    this.createElements();
    this.startTimer();
  }

  disconnectedCallback() {
    if (this.timer) {
      clearInterval(this.timer);
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'target-date' && oldValue !== newValue) {
      this.startTimer();
    }
    if (name === 'translations' && oldValue !== newValue) {
      this.loadTranslations();
      this.updateDisplay();
    }
    if (name === 'hide-when-expired' && oldValue !== newValue) {
      this.hideWhenExpired = newValue === 'true';
    }
  }

  loadTranslations() {
    const translationsAttr = this.getAttribute('translations');
    if (translationsAttr) {
      try {
        const customTranslations = JSON.parse(translationsAttr);
        this.translations = {
          ...this.translations,
          ...customTranslations
        };
      } catch (e) {
        console.error('Invalid translations format:', e);
      }
    }
  }

  createElements() {
    // Get slotted content before clearing
    const titleSlot = this.querySelector('[slot="title"]');
    const expiredSlot = this.querySelector('[slot="expired"]');

    // Container
    const container = document.createElement('div');
    container.className = 'ds-timer__container';

    // Title from slot (richtext)
    if (titleSlot) {
      const titleWrapper = document.createElement('div');
      titleWrapper.className = 'ds-timer__title';
      titleWrapper.appendChild(titleSlot.cloneNode(true));
      titleSlot.remove(); // Remove from light DOM
      container.appendChild(titleWrapper);
      this.elements.title = titleWrapper;
    }

    const countdownContainer = document.createElement('div');
    countdownContainer.className = 'ds-timer__countdown';
    this.elements.countdownContainer = countdownContainer;

    // Create units (days, hours, minutes, seconds)
    const units = ['days', 'hours', 'minutes', 'seconds'];

    units.forEach((unit, index) => {
      // Create item container
      const item = document.createElement('div');
      item.className = 'ds-timer__item';

      // Create number element
      const number = document.createElement('span');
      number.className = 'ds-timer__number';
      this.elements[`${unit}-number`] = number;

      // Create label element
      const label = document.createElement('span');
      label.className = 'ds-timer__label';
      this.elements[`${unit}-label`] = label;

      // Add number and label to item
      item.appendChild(number);
      item.appendChild(label);

      // Add item to container
      countdownContainer.appendChild(item);

      // Add separator if not last item
      if (index < units.length - 1) {
        const separator = document.createElement('div');
        separator.className = 'ds-timer__separator';
        separator.textContent = ':';
        countdownContainer.appendChild(separator);
      }
    });

    // Create expired message element from slot (hidden by default)
    const expiredMessage = document.createElement('div');
    expiredMessage.className = 'ds-timer__expired-message';
    expiredMessage.style.display = 'none';

    if (expiredSlot) {
      expiredMessage.appendChild(expiredSlot.cloneNode(true));
      expiredSlot.remove(); // Remove from light DOM
      this.hasExpiredContent = true;
    } else {
      this.hasExpiredContent = false;
    }

    this.elements.expiredMessage = expiredMessage;

    // Add countdown container to main container
    container.appendChild(countdownContainer);
    container.appendChild(expiredMessage);

    // Store reference to main container
    this.elements.container = container;

    // Clear and append new content
    this.replaceChildren(container);
  }

  calculateTimeLeft() {
    const targetDate = new Date(this.getAttribute('target-date'));
    const now = new Date();
    const difference = targetDate.getTime() - now.getTime();

    if (difference <= 0) {
      this.dispatchEvent(new CustomEvent('countdown-complete'));
      return {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0
      };
    }

    return {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / 1000 / 60) % 60),
      seconds: Math.floor((difference / 1000) % 60)
    };
  }

  startTimer() {
    if (this.timer) {
      clearInterval(this.timer);
    }

    this.timeLeft = this.calculateTimeLeft();
    this.updateDisplay();

    // Check if already expired on load
    if (Object.values(this.timeLeft).every(value => value === 0)) {
      this.handleExpired();
      return;
    }

    // Timer is still active - show wrapper if it was hidden
    // (wrapper starts hidden when hide_when_expired is enabled to prevent flash)
    const wrapper = this.closest('.ds-timer__wrapper');
    if (wrapper && this.hideWhenExpired) {
      wrapper.style.display = 'flex';
    }

    this.timer = setInterval(() => {
      this.timeLeft = this.calculateTimeLeft();
      this.updateDisplay();

      if (Object.values(this.timeLeft).every(value => value === 0)) {
        clearInterval(this.timer);
        this.handleExpired();
      }
    }, 1000);
  }

  handleExpired() {
    if (this.isExpired) return;
    this.isExpired = true;

    // Get the wrapper element (parent of this component)
    const wrapper = this.closest('.ds-timer__wrapper');

    if (this.hideWhenExpired) {
      // Hide the entire timer wrapper
      if (wrapper) {
        wrapper.style.display = 'none';
      } else {
        this.style.display = 'none';
      }
    } else if (this.hasExpiredContent) {
      // Show expired content instead of countdown
      if (this.elements.countdownContainer) {
        this.elements.countdownContainer.style.display = 'none';
      }
      if (this.elements.title) {
        this.elements.title.style.display = 'none';
      }
      if (this.elements.expiredMessage) {
        this.elements.expiredMessage.style.display = 'block';
      }
    }
    // If neither option is set, timer stays at 00:00:00:00
  }

  padNumber(num) {
    return String(num).padStart(2, '0');
  }

  getLabel(unit, value) {
    const translation = this.translations[unit];
    if (!translation) return '';
    return value === 1 ? translation.singular : translation.plural;
  }

  updateDisplay() {
    Object.entries(this.timeLeft).forEach(([unit, value]) => {
      // Update number
      const numberElement = this.elements[`${unit}-number`];
      if (numberElement) {
        numberElement.textContent = this.padNumber(value);
      }

      // Update label with singular/plural form
      const labelElement = this.elements[`${unit}-label`];
      if (labelElement) {
        labelElement.textContent = this.getLabel(unit, value);
      }
    });
  }
}

// Register the web component
customElements.define('countdown-timer', CountdownTimer);
